import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

export const sectionsList = [
  'abstractExpanded',
  'indextermsExpanded',
  'claimsExpanded',
  'bibliographicInfoExpanded',
  'substancesExpanded',
  'reactionsExpanded',
  'targetsExpanded',
  'hitSubstancesExpanded',
  'hitReactionsExpanded',
];

export const displayConditions = {
  abstractExpanded: (citation) => Boolean(citation.abstractText),
  indextermsExpanded: (citation) => Boolean(citation.keywords),
  claimsExpanded: (citation) => Boolean(citation.patentClaims),
  bibliographicInfoExpanded: (citation) =>
    Boolean(citation.assignees || citation.inventors || citation.priorityData || citation.patentFamily),
  substancesExpanded: (citation) => Boolean(citation.substancesCount),
  reactionsExpanded: (citation) => {
    const { reactionsCount } = citation;
    return Boolean(reactionsCount) && !Auth.isRMCLicense();
  },
  targetsExpanded: (citation) => {
    const { targetsCount } = citation;
    return Boolean(targetsCount) && !Auth.isClassicLicense();
  },
  hitSubstancesExpanded: (citation) => Boolean(citation.substancesHitCount),
  hitReactionsExpanded: (citation) => {
    const { reactionsHitCount } = citation;
    return Boolean(reactionsHitCount) && !Auth.isRMCLicense();
  },
};

export const getFirstAvailableSection = (citation) => sectionsList.find((section) => displayConditions[section](citation));

export const detailNavDataE2eById = {
  abstractExpanded: 'e2e-document-action-abstract',
  indextermsExpanded: 'e2e-document-action-index terms',
  claimsExpanded: 'e2e-document-action-claims',
  bibliographicInfoExpanded: 'e2e-document-action-bibliographic info',
  substancesExpanded: 'e2e-document-action-substances',
  reactionsExpanded: 'e2e-document-action-reactions',
  targetsExpanded: 'e2e-document-action-targets',
  hitSubstancesExpanded: 'e2e-document-action-hit substances',
  hitReactionsExpanded: 'e2e-document-action-hit reactions',
};

export const getDetailNavDataE2e = (id) => detailNavDataE2eById[id];

const getBibliographicTitle = () =>
  !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) ? 'Bibliographic Info' : 'Front Page Info';

/**
 * Ordered list of toggle-able nav categories for the details navigation.
 * Each item: { id, title, count?, dataE2e }. Filtered to the categories that have
 * data. The matched-text hit previews (Abstract Hit / Index Terms Hit) are NOT
 * nav items — they render as the default view when nothing is open. "Full Text"
 * is also not included here — it is an external link appended by DocumentButtons.
 */
export const buildDetailNavItems = (citation) =>
  [
    { id: 'abstractExpanded', title: 'Abstract', dataE2e: detailNavDataE2eById.abstractExpanded },
    { id: 'indextermsExpanded', title: 'Index Terms', dataE2e: detailNavDataE2eById.indextermsExpanded },
    { id: 'claimsExpanded', title: 'Claims', dataE2e: detailNavDataE2eById.claimsExpanded },
    {
      id: 'bibliographicInfoExpanded',
      title: getBibliographicTitle(),
      dataE2e: detailNavDataE2eById.bibliographicInfoExpanded,
    },
    {
      id: 'substancesExpanded',
      title: 'Substances',
      count: citation.substancesCount,
      dataE2e: detailNavDataE2eById.substancesExpanded,
    },
    { id: 'reactionsExpanded', title: 'Reactions', count: citation.reactionsCount, dataE2e: detailNavDataE2eById.reactionsExpanded },
    { id: 'targetsExpanded', title: 'Targets', dataE2e: detailNavDataE2eById.targetsExpanded },
    {
      id: 'hitSubstancesExpanded',
      title: 'Hit Substances',
      count: citation.substancesHitCount,
      dataE2e: detailNavDataE2eById.hitSubstancesExpanded,
    },
    {
      id: 'hitReactionsExpanded',
      title: 'Hit Reactions',
      count: citation.reactionsHitCount,
      dataE2e: detailNavDataE2eById.hitReactionsExpanded,
    },
  ]
    .filter((item) => displayConditions[item.id](citation))
    .map((item, index, items) => {
      const firstHitIndex = items.findIndex(({ id }) => id === 'hitSubstancesExpanded' || id === 'hitReactionsExpanded');
      return index === firstHitIndex ? { ...item, separatorLeft: true } : item;
    });
